import { Button } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import {
  AttachmentPreviewList,
  MessageInputProps,
  useAttachmentManagerState,
  useMessageComposer,
  useMessageContext,
  useMessageInputContext,
} from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { ErrorText } from 'src/components/ErrorText';
import {
  getCharacterLimitError,
  MAX_CHARACTERS_LIMIT,
} from 'src/constants/messageConsts';
import { MessageRichTextInput } from 'src/features/channel/custom-message-input/MessageRichTextInput';
import { HoverBackground, NonHoverBorder } from 'src/theme/colors';
import { isEmptyString } from 'src/utils/StringUtils';

import { useMessageComponents } from './useMessageComponents';

interface StylesProps {
  showAvatar: boolean;
}
const useStyles = makeStyles<StylesProps>()((theme, props) => ({
  editMessageFormWrapper: {
    padding: theme.spacing(1.5, 9, 6.25, props.showAvatar ? 3.5 : 9),
    backgroundColor: HoverBackground,
    '& .rfu-image-previewer': {
      margin: 0,
    },

    '& .str-chat__textarea textarea': {
      border: `1px solid ${NonHoverBorder}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1, 1.5),
      '&:focus': {
        border: `1px solid ${NonHoverBorder}`,
        boxShadow: 'none',
      },
    },
  },
  submitButton: {
    marginLeft: theme.spacing(2.5),
  },
}));

interface EditMessageFormProps {
  handleDelete: () => void;
}

export const EditMessageForm: React.FC<
  EditMessageFormProps & MessageInputProps
> = ({ handleDelete }) => {
  const { avatarRenderer, isNewMessageGroup: showAvatar } =
    useMessageComponents();
  const { classes } = useStyles({ showAvatar: !!showAvatar });
  const { handleSubmit, clearEditingState } = useMessageInputContext();
  const { message } = useMessageContext();
  const { isUploadEnabled, successfulUploadsCount } =
    useAttachmentManagerState();
  const messageComposer = useMessageComposer();
  const { text } = messageComposer.textComposer;
  const { attachments } = messageComposer.attachmentManager;
  const [draftAttachments, setDraftAttachments] = React.useState<
    typeof attachments
  >(() => []);
  const [originalText, setOriginalText] = React.useState('');

  const handleRichTextContentChange = async (editor: Editor) => {
    await messageComposer.textComposer.handleChange({
      selection: {
        end: editor.state.selection.$to.pos,
        start: editor.state.selection.$from.pos,
      },
      text: editor.storage.markdown.getMarkdown() ?? '',
    });
  };

  const handleEdit = (event: React.BaseSyntheticEvent) => {
    event.preventDefault();
    if (text.length > MAX_CHARACTERS_LIMIT) {
      return;
    }
    const isEmptyMessageText = isEmptyString(text);
    if (isEmptyMessageText && successfulUploadsCount === 0) {
      handleDelete();
      return;
    }
    handleSubmit(event);
  };

  // Capture original values when a new message edit starts
  useEffect(() => {
    if (message?.id) {
      setDraftAttachments([...attachments]);
      setOriginalText(text);
    }
  }, [message?.id]);

  const handleCancel = () => {
    if (clearEditingState) {
      messageComposer.textComposer.handleChange({
        text: originalText,
        selection: {
          start: originalText.length,
          end: originalText.length,
        },
      });
      const uploadsIds = messageComposer.attachmentManager.attachments.map(
        (attachment) => attachment.localMetadata.id,
      );
      messageComposer.attachmentManager.removeAttachments(uploadsIds);
      messageComposer.attachmentManager.upsertAttachments(draftAttachments);
      clearEditingState();
    }
  };

  return (
    <div className={classes.editMessageFormWrapper}>
      <form onSubmit={handleEdit}>
        <div className="flex w-full items-start">
          {showAvatar && avatarRenderer()}
          <div
            className={clsx('w-full', {
              'ml-3': showAvatar,
              'ml-0': !showAvatar,
            })}
          >
            {isUploadEnabled && <AttachmentPreviewList />}
            <div className="border border-solid border-non-hover-border focus-within:border-black-heading rounded-xl bg-white">
              <MessageRichTextInput
                key={`edit-rich-textarea-${message?.id}`}
                content={message?.html ?? message?.text ?? ''}
                onContentChange={handleRichTextContentChange}
              />
            </div>
          </div>
        </div>
        {text.length > MAX_CHARACTERS_LIMIT && (
          <div className="flex justify-end">
            <ErrorText>{getCharacterLimitError(text.length)}</ErrorText>
          </div>
        )}
        <div className="mt-3 gap-5 flex w-full items-center justify-end">
          <Button variant="secondary" label="Cancel" onClick={handleCancel} />
          <Button
            variant="primary"
            label="Save changes"
            type="submit"
            disabled={text.length > MAX_CHARACTERS_LIMIT}
          />
        </div>
      </form>
    </div>
  );
};
